import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Heart } from 'lucide-react';

interface FavoriteButtonProps {
  onClick: () => void;
  isFavorited: boolean;
  componentId: string;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
}

const FavoriteButton: React.FC<FavoriteButtonProps> = ({ 
  onClick, 
  isFavorited,
  componentId,
  className = '',
  size = 'md'
}) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  
  const handleClick = () => {
    setIsAnimating(true);
    onClick();
    
    // Haptic feedback for mobile
    if ('vibrate' in navigator) {
      navigator.vibrate(50);
    }
    
    // Reset animation after completion
    setTimeout(() => setIsAnimating(false), 600);
  };
  
  const handleMouseEnter = () => {
    setIsHovering(true);
    // Calculate tooltip position
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setTooltipPosition({
        top: rect.top - 40, // 40px above the button
        left: rect.left + rect.width / 2
      });
    }
    // Show tooltip after a short delay
    timeoutRef.current = setTimeout(() => {
      setShowTooltip(true);
    }, 500);
  };
  
  const handleMouseLeave = () => {
    setIsHovering(false);
    setShowTooltip(false);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  };
  
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);
  
  const sizeClasses = {
    sm: 'h-8 w-10',
    md: 'h-10 w-14',
    lg: 'h-12 w-16'
  };
  
  const iconSizes = {
    sm: 20,
    md: 24,
    lg: 28
  };
  
  return (
    <>
      {/* Tooltip Portal */}
      {showTooltip && createPortal(
        <div 
          className="fixed transform -translate-x-1/2 z-[99999] pointer-events-none"
          style={{ 
            top: `${tooltipPosition.top}px`,
            left: `${tooltipPosition.left}px`,
          }}
        >
          <div className="bg-gray-800 text-white text-sm px-3 py-2 rounded-md whitespace-nowrap shadow-md animate-fadeIn">
            {isFavorited ?'حذف از داشبورد شخصی' : 'افزودن به داشبورد شخصی'}
            <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
              <div className="w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[6px] border-t-gray-800"></div>
            </div>
          </div>
        </div>,
        document.body
      )}
      
      <button
        ref={buttonRef}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className={`
          relative inline-flex items-center justify-center rounded-full
          transition-all duration-300 transform
          ${sizeClasses[size]}
          ${isFavorited 
            ? 'bg-red-500/10' 
            : 'bg-bg-secondary'
          }
          ${isHovering && !isFavorited ? 'bg-bg-tertiary scale-110' : ''}
          ${isHovering && isFavorited ? 'bg-red-500/20 scale-110' : ''}
          ${isAnimating ? 'scale-125' : ''}
          hover:shadow-lg
          ${className}
        `}
        aria-label={isFavorited ? 'حذف از داشبورد شخصی' : 'افزودن به داشبورد شخصی'}
        data-component-id={componentId}
      >
        {/* Heart Icon with Instagram-like animation */}
        <Heart 
          size={iconSizes[size]}
          className={`
            transition-all duration-300
            ${isFavorited 
              ? 'text-red-500 fill-red-500' 
              : 'text-text-secondary'
            }
            ${isHovering && !isFavorited ? 'text-text-primary scale-110' : ''}
            ${isAnimating ? 'animate-ping' : ''}
          `}
          style={{
            filter: isAnimating ? 'drop-shadow(0 0 8px rgba(239, 68, 68, 0.5))' : '',
            transform: isHovering ? 'rotateY(180deg)' : 'rotateY(0deg)',
            transformStyle: 'preserve-3d',
            transition: 'all 0.3s ease-in-out'
          }}
        />
        
        {/* Instagram-like burst animation */}
        {isAnimating && !isFavorited && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="w-full h-full rounded-full bg-red-500/30 animate-ping absolute"></div>
            <div className="w-full h-full rounded-full bg-red-500/20 animate-ping absolute" style={{ animationDelay: '0.1s' }}></div>
          </div>
        )}
      </button>
    </>
  );
};

export default FavoriteButton;